import { useEffect, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

import { getImageUrl } from "src/Common/common";

// Date-range options. `bounds(now)` returns inclusive [start, end] Date
// pairs for the matching window.
export const RANGE_OPTIONS = [
  {
    key: "thisMonth",
    label: "This Month",
    bounds: (now) => {
      const y = now.getFullYear();
      const m = now.getMonth();
      return [new Date(y, m, 1), new Date(y, m + 1, 0, 23, 59, 59, 999)];
    },
  },
  {
    key: "thisQuarter",
    label: "This Quarter",
    bounds: (now) => {
      const y = now.getFullYear();
      const qStart = Math.floor(now.getMonth() / 3) * 3;
      return [new Date(y, qStart, 1), new Date(y, qStart + 3, 0, 23, 59, 59, 999)];
    },
  },
  {
    key: "thisYear",
    label: "This Year",
    bounds: (now) => {
      const y = now.getFullYear();
      return [new Date(y, 0, 1), new Date(y, 11, 31, 23, 59, 59, 999)];
    },
  },
  {
    key: "nextYear",
    label: "Next Year",
    bounds: (now) => {
      const y = now.getFullYear() + 1;
      return [new Date(y, 0, 1), new Date(y, 11, 31, 23, 59, 59, 999)];
    },
  },
];

// "YYYY-MM-DD" in local time
const toIsoDay = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Resolve a range key to backend query params { startDate, endDate }.
export const rangeToParams = (key) => {
  const opt = RANGE_OPTIONS.find((o) => o.key === key);
  if (!opt) return null;
  const [start, end] = opt.bounds(new Date());
  return { startDate: toIsoDay(start), endDate: toIsoDay(end) };
};

// Top-right date-range dropdown for the Time Off page.
// `value` is one of the RANGE_OPTIONS keys; `onChange(key)` fires on select.
function TimeOffFilter({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const current = RANGE_OPTIONS.find((o) => o.key === value) || RANGE_OPTIONS[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between px-1 gap-1.5 w-[150px] h-[30px] rounded-lg border border-[#C9CEE2] bg-white hover:bg-[#F9FAFB] focus:outline-none"
      >
        <img src={getImageUrl("/images/common/calendar.svg")} className="w-[16.125px] h-[13.125px]" alt="" />
        <span className="text-center text-[13px] font-OpenSansSemiBold text-[#1A2332] leading-6 capitalize flex-1">
          {current.label}
        </span>
        <FiChevronDown
          size={16}
          className={`text-[#1E293B] transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 rounded-lg border border-[#E5E7EB] bg-white shadow-[0_10px_15px_-3px_rgba(0,0,0,0.10),0_4px_6px_-4px_rgba(0,0,0,0.10)] overflow-hidden z-10">
          {RANGE_OPTIONS.map((opt, i) => (
            <div key={opt.key}>
              {i > 0 && <div className="h-px bg-[#F3F4F6]" />}
              <button
                type="button"
                onClick={() => {
                  onChange?.(opt.key);
                  setOpen(false);
                }}
                className={`block w-full px-4 py-2 text-center whitespace-nowrap text-[13px] leading-6 capitalize font-OpenSansSemiBold text-[#5F6477] hover:bg-[#F9FAFB] ${
                  value === opt.key ? "bg-[#EEF0F9]" : ""
                }`}
              >
                {opt.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TimeOffFilter;
